import { anonymousPortfolioPlaceholder } from "@/config/portfolio";
import {
  BlockchainType,
  ContentVisibility,
  ExchangeProvider,
  PortfolioSourceType,
  TransactionDirection,
  TransactionType,
  WithdrawReason,
} from "@prisma/client";
import { z } from "zod";
import {
  AssetInputSchema,
  AssetSchema,
  BaseAssetSchema,
  BaseAssetWithQntPriceAmountSchema,
} from "../assets/schema";
import { userSchema } from "../users/schema";

const BlockchainTypeSchema = z.nativeEnum(BlockchainType);
const ExchangeProviderSchema = z.nativeEnum(ExchangeProvider);
const SourceTypeSchema = z.nativeEnum(PortfolioSourceType);

const baseSourceFields = {
  type: SourceTypeSchema,
  isActive: z.boolean().default(true),
  id: z.string().cuid().optional(),
};

export const ExchangeConnectionSchema = z.object({
  ...baseSourceFields,
  type: z.literal(PortfolioSourceType.EXCHANGE_API),
  provider: ExchangeProviderSchema,
  apiKey: z.string().min(1),
  secretKey: z.string().min(1),
  passphrase: z.string().optional(),
  extraParams: z.record(z.string(), z.unknown()).nullish(),
});
export type ExchangeConnection = z.infer<typeof ExchangeConnectionSchema>;

// Wallet connection schema
export const WalletConnectionSchema = z.object({
  ...baseSourceFields,
  type: z.literal(PortfolioSourceType.CRYPTO_WALLET),
  address: z.string().min(1),
  chain: BlockchainTypeSchema,
  chainId: z.number().int().positive(),
});
export type WalletConnection = z.infer<typeof WalletConnectionSchema>;

// CSV import schema
export const CsvImportSchema = z.object({
  ...baseSourceFields,
  type: z.literal(PortfolioSourceType.CSV_IMPORT),
  fileName: z.string().min(1),
  lastImportedAt: z.date(),
});
export type CsvImport = z.infer<typeof CsvImportSchema>;

// Manual source schema
export const ManualSourceSchema = z.object({
  ...baseSourceFields,
  type: z.literal(PortfolioSourceType.MANUAL),
});
export type ManualSource = z.infer<typeof ManualSourceSchema>;

export const PortfolioSourceSchema = z.discriminatedUnion("type", [
  ManualSourceSchema,
  ExchangeConnectionSchema,
  WalletConnectionSchema,
  CsvImportSchema,
]);
export type PortfolioSource = z.infer<typeof PortfolioSourceSchema>;

export const PortfolioSchema = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().default(""),
  startingCapital: z.coerce.number().default(0),
  visibility: z.nativeEnum(ContentVisibility).default(ContentVisibility.PUBLIC),
  isAuthorVisible: z.boolean().default(false),
  isPortfolioValueVisible: z.boolean().default(false),
  portfolioDetailsSubscriptionPrice: z.coerce
    .number()
    .nullish()
    .transform((v) => v ?? undefined),
  portfolioAlertsSubscriptionPrice: z.coerce
    .number()
    .nullish()
    .transform((v) => v ?? undefined),
  assets: z.array(AssetSchema).optional().default([]),
  userId: z.string().cuid().optional(),
  user: userSchema.pick({ id: true, name: true, image: true }).optional(),
  createdAt: z.coerce.date().optional(),
  notificationPreferences: z
    .union([
      z.boolean().nullish().default(false),
      z
        .object({
          enabled: z.boolean(),
        })
        .nullish()
        .transform((v) => Boolean(v?.enabled)),
    ])
    .default(false),
  tournamentId: z.string().cuid().nullish(),
  tournament: z
    .object({
      id: z.string().cuid(),
      name: z.string(),
      startDate: z.coerce.date().nullish(),
      endDate: z.coerce.date().nullish(),
    })
    .nullish(),

  portfolioTransaction: z
    .object({
      totalAmount: z.coerce.number().optional(),
      totalQuantity: z.coerce.number().optional(),
      totalDeposit: z.coerce.number().optional(),
    })
    .nullish(),
  sources: z.array(PortfolioSourceSchema).optional().default([]),
});

export const PortfolioInputSchema = PortfolioSchema.omit({
  id: true,
  userId: true,
  user: true,
  createdAt: true,
  notificationPreferences: true,
  tournamentId: true,
  tournament: true,
  portfolioTransaction: true,
  assets: true,
}).extend({
  assets: z.array(AssetInputSchema).optional().default([]),
});

function transformNumber(val: number) {
  if (isNaN(val)) return null;
  if (val === Number.POSITIVE_INFINITY || val === Number.NEGATIVE_INFINITY)
    return Number.MAX_SAFE_INTEGER * -1;

  return val;
}
export const PortfolioToLeaderboardPortfolioRowSchema = z
  .object({
    id: z.string().optional(),
    name: z.string(),
    visibility: z
      .nativeEnum(ContentVisibility)
      .optional()
      .default(ContentVisibility.PUBLIC),
    isAuthorVisible: z.boolean().default(false),
    assets: z.array(AssetSchema).optional().default([]),
    user: z.object({
      name: z.string().optional(),
      id: z.string().cuid().optional(),
    }),
    balance: z.coerce.number().default(0),

    last7Days: z.number().default(0),
    pctChange: z.object({
      d: z.coerce.number().transform(transformNumber).default(0),
      w: z.coerce.number().transform(transformNumber).default(0),
      m: z.coerce.number().transform(transformNumber).default(0),
      y: z.coerce.number().transform(transformNumber).default(0),
      t: z.coerce.number().transform(transformNumber).default(0),
      ytd: z.coerce.number().transform(transformNumber).default(0),
      tournament: z.coerce.number().transform(transformNumber).default(0),
    }),

    cagr: z.number().nullable().default(0),
    sharpe: z.number().nullable().default(0),
    maxDrawdown: z.number().nullable().default(0),
    tournament: z
      .object({
        id: z.string().cuid(),
        name: z.string(),
      })
      .nullish(),

    createdAt: z.coerce.date().optional(),
  })
  .transform((data) => {
    return {
      id: data.id,
      name: data.name,
      visibility: data.visibility,
      author: data.isAuthorVisible
        ? data.user?.name
        : anonymousPortfolioPlaceholder,
      authorId: data.isAuthorVisible ? data.user?.id : undefined,
      positions: data.assets.length,
      assets: data.assets,
      last7Days: data.last7Days,
      dPercentage: data.pctChange.d,
      wPercentage: data.pctChange.w,
      mPercentage: data.pctChange.m,
      yPercentage: data.pctChange.y,
      ytdPercentage: data.pctChange.ytd,
      tPercentage: data.pctChange.t,
      tournamentPercentage: data.pctChange.tournament,
      cagr: data.cagr,
      sharpe: data.sharpe,
      maxDrawdown: data.maxDrawdown,
      balance: data.balance,
      tournament: data.tournament,
      createdAt: data.createdAt,
    };
  });
export const LeaderboardPortfolioRowSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  author: z.string().optional().default("Anonymous"),
  authorId: z.string().cuid().optional(),
  positions: z.number().optional().default(0),
  assets: z.array(AssetSchema).optional().default([]),
  last7Days: z.number().default(0),
  dPercentage: z.number().nullable().default(0),
  mPercentage: z.number().nullable().default(0),
  wPercentage: z.number().nullable().default(0),
  yPercentage: z.number().nullable().default(0),
  tPercentage: z.number().nullable().default(0),
  tournamentPercentage: z.number().nullable().default(0),
  cagr: z.number().nullable().default(0),
  sharpe: z.number().nullable().default(0),
  maxDrawdown: z.number().nullable().default(0),
  balance: z.coerce.number().default(0),
});

export const PortfolioSearchSchema = PortfolioSchema.pick({
  id: true,
  name: true,
  user: true,
  isAuthorVisible: true,
});

const transformChange = (val: unknown) => {
  const valueNumber = Number(val);
  if (isNaN(valueNumber)) return null;
  if (
    valueNumber === Number.POSITIVE_INFINITY ||
    valueNumber === Number.NEGATIVE_INFINITY
  ) {
    return null;
  }

  return valueNumber;
};

export const PortfolioMetricsSchema = PortfolioSchema.pick({
  id: true,
}).extend({
  return: z.object({
    d: z
      .union([z.number(), z.coerce.number()])
      .transform(transformChange)
      .nullable(),
    w: z
      .union([z.number(), z.coerce.number()])
      .transform(transformChange)
      .nullable(),

    m: z
      .union([z.number(), z.coerce.number()])
      .transform(transformChange)
      .nullable(),
    y: z
      .union([z.number(), z.coerce.number()])
      .transform(transformChange)
      .nullable(),
    ytd: z
      .union([z.number(), z.coerce.number()])
      .transform(transformChange)
      .nullable(),
    t: z
      .union([z.number(), z.coerce.number()])
      .transform(transformChange)
      .nullable(),
    tournament: z
      .union([z.number(), z.coerce.number()])
      .transform(transformChange)
      .nullable(),
    cagr: z.number().nullable(),
  }),

  ratio: z.object({
    sharpe: z.number().nullable(),
    maxDrawdown: z.number().nullable(),
  }),
});
export function parsePortfolioMetrics(data: unknown) {
  const parseResult = PortfolioMetricsSchema.safeParse(data);

  if (!parseResult.success) {
    console.error(parseResult.error);
  }

  return parseResult.success ? parseResult.data : null;
}

const PortfolioHistoricalAssetValue = z.object({
  quantity: z.union([z.number(), z.coerce.number()]),
  price: z.number(),
  timestamp: z.number(),
  value: z.union([z.number(), z.coerce.number()]),
});

const PortfolioHistoricalValueSchema = z.object({
  timestamp: z.number(),
  value: z.coerce.number(),
});

const PortfolioValueSchema = z
  .object({
    value: z.coerce.number(),
    createdAt: z.coerce.date(),
  })
  .transform((data) => {
    return {
      value: data.value,
      timestamp: data.createdAt.getTime(),
    };
  });

const PortfolioHistoricalAssetValueRecordSchema = z.record(
  z.string(),
  PortfolioHistoricalAssetValue,
);
const PortfolioHistoricalAssetValueListSchema = z.record(
  z.string(),
  PortfolioHistoricalAssetValueRecordSchema,
);

export type PortfolioHistoricalAssetValueRecord = z.infer<
  typeof PortfolioHistoricalAssetValueRecordSchema
>;

export const PortfolioPerformanceSchema = z.object({
  portfolioHistoricalValues: z.array(
    z.object({
      timestamp: z.number(),
      value: z.coerce.number(),
    }),
  ),
  portfolioHistoricalAssetValues: PortfolioHistoricalAssetValueListSchema,
});
export function parsePortfolioPerformance(data: unknown) {
  const parseResult = PortfolioPerformanceSchema.safeParse(data);

  if (!parseResult.success) {
    console.error(parseResult.error);
  }

  return parseResult.success ? parseResult.data : null;
}

const PortfolioPctChangeSchema = z.object({
  currentValue: z.coerce.number(),
  previousValue: z.coerce.number(),
  pctChange: z.coerce.number(),
});

export const PortfolioHistoryTransactionSchema = z.object({
  asset: z.object({
    symbol: z.string(),
  }),
  cost: z.coerce.number(),
  amount: z.coerce.number(),
  price: z.coerce.number(),
  direction: z.nativeEnum(TransactionDirection),
  type: z.nativeEnum(TransactionType),
  portfolioId: z.string().cuid(),
  userId: z.string(),
  createdAt: z.coerce.date(),
  note: z.string().nullish(),
});

export const PortfoliosCountSchema = z.object({
  count: z.number().nonnegative(),
  max: z.number().nonnegative(),
});

export type Portfolio = z.infer<typeof PortfolioSchema>;
export type LeaderboardPortfolioRow = z.infer<
  typeof PortfolioToLeaderboardPortfolioRowSchema
>;

export type PortfolioSearch = z.infer<typeof PortfolioSearchSchema>;
export type PortfolioPerformance = z.infer<typeof PortfolioPerformanceSchema>;
export type PortfolioMetrics = z.infer<typeof PortfolioMetricsSchema>;

export type PortfolioHistoryTransaction = z.infer<
  typeof PortfolioHistoryTransactionSchema
>;
export type PortfoliosCount = z.infer<typeof PortfoliosCountSchema>;

export function parsePortfolio(data: unknown) {
  const parseResult = PortfolioSchema.safeParse(data);

  if (!parseResult.success) {
    console.error("INVALID PORTFOLIO DATA", parseResult.error.issues);
  }

  return parseResult.success ? parseResult.data : null;
}

export const PortfoliosSchema = z.array(PortfolioSchema);
export function parsePortfolios(data: unknown) {
  const parseResult = PortfoliosSchema.safeParse(data);

  if (!parseResult.success) {
    console.warn("INVALID PORTFOLIOS DATA", data, parseResult.error.issues);
  }

  return parseResult.success ? parseResult.data : null;
}

export const LeaderboardPortfolioRowsSchema = z.array(
  LeaderboardPortfolioRowSchema,
);

export function parseLeaderboardPortfolioRows(data: unknown) {
  const parseResult = LeaderboardPortfolioRowsSchema.safeParse(data);

  if (!parseResult.success) {
    console.warn(
      "INVALID LEADERBOARD PORTFOLIO ROWS DATA",
      data,
      parseResult.error,
    );
  }

  return (
    parseResult.success ? parseResult.data : []
  ) as LeaderboardPortfolioRow[];
}

export function parsePortfoliosToLeaderboardPortfolioRows(
  data: unknown,
): LeaderboardPortfolioRow[] | undefined {
  if (Array.isArray(data)) {
    return data
      .map((portfolio) => {
        const parseResult =
          PortfolioToLeaderboardPortfolioRowSchema.safeParse(portfolio);

        return parseResult.success ? parseResult.data : null;
      })
      .filter(Boolean);
  } else {
    return [];
  }
}

export function parsePortfoliosToDashboardPortfolioRows(data: unknown) {
  if (Array.isArray(data)) {
    return data
      .map((portfolio) => {
        const parseResult = PortfolioSchema.safeParse(portfolio);

        if (!parseResult.success) console.error(parseResult.error);

        return parseResult.success ? parseResult.data : null;
      })
      .filter(Boolean);
  }
}

const PortfolioSearchListSchema = z.array(PortfolioSearchSchema);
export function parsePortfolioSearches(data: unknown) {
  const parseResult = PortfolioSearchListSchema.safeParse(data);

  return parseResult.success ? parseResult.data : null;
}

export function parsePortfolioHistoricalAssetValues(data: unknown) {
  const parseResult = PortfolioHistoricalAssetValueListSchema.safeParse(data);

  return parseResult.success ? parseResult.data : null;
}

const PortfolioValueListSchema = z.array(PortfolioValueSchema);
export function parsePortfolioValues(data: unknown) {
  const parseResult = PortfolioValueListSchema.safeParse(data);

  if (!parseResult.success) {
    console.error(parseResult.error);
  }

  return parseResult.success ? parseResult.data : null;
}

const PortfolioHistoricalValueListSchema = z.array(
  PortfolioHistoricalValueSchema,
);
export function parsePortfolioHistoricalValues(data: unknown) {
  const parseResult = PortfolioHistoricalValueListSchema.safeParse(data);

  if (!parseResult.success) {
    console.error(parseResult.error);
  }

  return parseResult.success ? parseResult.data : null;
}

export function parsePortfolioPctChange(data: unknown) {
  const parseResult = PortfolioPctChangeSchema.safeParse(data);

  return parseResult.success ? parseResult.data : null;
}

const IsOnWatchlistSchema = z.object({
  isOnWatchlist: z.boolean(),
});
export function parseIsOnWatchlist(data: unknown) {
  const parseResult = IsOnWatchlistSchema.safeParse(data);

  return parseResult.success ? parseResult.data : null;
}

const BalanceSchema = z.object({
  currentBalance: z.coerce.number(),
});
export function parseBalance(data: unknown) {
  const parseResult = BalanceSchema.safeParse(data);

  return parseResult.success ? parseResult.data : null;
}

const PortfolioHistorySchema = z.array(PortfolioHistoryTransactionSchema);
export function parsePortfolioHistory(data: unknown) {
  const result = PortfolioHistorySchema.safeParse(data);
  if (!result.success) {
    console.error(result.error.issues);
  }

  return result.success ? result.data : [];
}

export function parsePortfoliosCount(data: unknown) {
  const result = PortfoliosCountSchema.safeParse(data);

  return result.success ? result.data : null;
}

export const PortfolioAssetSchema = z
  .object({
    quantity: z.coerce
      .number({
        message: "Quantity must be a number",
      })
      .positive()
      .optional(),
    amount: z.coerce
      .number({
        message: "Amount must be a number",
      })
      .positive()
      .optional(),
    transactionType: z.nativeEnum(TransactionType),
    createdAt: z.coerce.date().default(new Date()),
    price: z.coerce.number().positive().optional(),
    fromBalance: z.boolean().optional(),
    toBalance: z.boolean().optional(),
    targetAsset: BaseAssetWithQntPriceAmountSchema.optional(),
    feeQuantity: z.coerce
      .number({
        message: "Amount must be a number",
      })
      .nonnegative()
      .optional(),
    feeAsset: BaseAssetSchema.optional(),
    note: z.string().optional(),
    withdrawReason: z.nativeEnum(WithdrawReason).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.transactionType !== TransactionType.CLOSE &&
      data.quantity === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "You must fill in either the amount or quantity field",
        path: ["amount"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "You must fill in either the amount or quantity field",
        path: ["quantity"],
      });
    }
  });

export type PortfolioAssetFormValues = z.infer<typeof PortfolioAssetSchema>;
